import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const teamMembers = [
    {
      name: 'David Wilson',
      role: 'Head Guide',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
      bio: 'With over 20 years of experience, David leads our team of expert guides.'
    },
    {
      name: 'Emma Thompson',
      role: 'Senior Guide',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
      bio: 'Emma specialises in fly fishing and has won numerous competitions.'
    },
    {
      name: 'James Anderson',
      role: 'Equipment Specialist',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
      bio: 'James ensures all our equipment is top-quality and well-maintained.'
    }
  ];

  const values = [
    {
      title: 'Sustainability',
      description: 'We are committed to preserving the natural beauty of Loch Lomond for future generations.',
      icon: '🌿'
    },
    {
      title: 'Excellence',
      description: 'We strive to provide the highest quality fishing experiences and customer service.',
      icon: '⭐'
    },
    {
      title: 'Community',
      description: 'We actively engage with and support the local community and fishing enthusiasts.',
      icon: '🤝'
    }
  ];

  const galleryImages = [
    {
      src: '/images/scenery 1.jpg',
      alt: 'Beautiful Loch Lomond Scenery',
      category: 'landscape'
    },
    {
      src: '/images/scenery2.jpg',
      alt: 'Stunning Mountain Views',
      category: 'landscape'
    },
    {
      src: '/images/scenery5.jpg',
      alt: 'Panoramic Loch Views',
      category: 'landscape'
    },
    {
      src: '/images/fishing111.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishing2.jpg',
      alt: 'Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishing3.jpg',
      alt: 'Fishing Techniques',
      category: 'fishing'
    },
    {
      src: '/images/fishing5.jpg',
      alt: 'Fishing Success',
      category: 'fishing'
    },
    {
      src: '/images/fishing7.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishing8.jpg',
      alt: 'Fishing Moment',
      category: 'fishing'
    },
    {
      src: '/images/fihsing9.jpg',
      alt: 'Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishing10.jpg',
      alt: 'Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat1.jpg',
      alt: 'Boat Fishing',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat2.jpg',
      alt: 'Boat Fishing Adventure',
      category: 'fishing'
    },
    {
      src: '/images/fishingboat3.jpg',
      alt: 'Boat Fishing Experience',
      category: 'fishing'
    },
    {
      src: '/images/outdoorrust.jpg',
      alt: 'Outdoor Adventure',
      category: 'landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Helmet>
        <title>About Us - Anchor Management</title>
        <meta name="description" content="Learn about Anchor Management's journey and commitment to providing exceptional fishing experiences at Loch Lomond" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/scenery2.jpg"
            alt="Loch Lomond fishing scene"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            About Anchor Management
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto"
          >
            Your trusted partner for unforgettable fishing experiences at Loch Lomond
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/scenery2.jpg"
                  alt="Anchor Management at Loch Lomond"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-800 dark:text-white">
                Discover the passion behind Anchor Management at Loch Lomond
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Founded in 2010, Anchor Management began with a simple mission: to share the beauty and excitement of fishing at Loch Lomond with enthusiasts from around the world. What started as a small family operation has grown into one of Scotland's premier fishing experience providers.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                Our team of experienced guides and passionate anglers are dedicated to providing unforgettable fishing adventures while promoting sustainable fishing practices and environmental conservation.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-gradient-to-r from-primary-600 to-primary-500 text-white px-8 py-4 rounded-full font-semibold hover:from-primary-700 hover:to-primary-600 transition-all transform hover:scale-105 shadow-lg"
              >
                Join Us Today
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-white">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl p-8 text-center hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                <div className="text-5xl mb-6 bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">{value.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">{value.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-white">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                  <p className="text-primary-600 dark:text-primary-400 mb-4">{member.role}</p>
                  <p className="text-gray-700 dark:text-gray-300">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        {/* Gallery Hero Section */}
        <div className="relative h-[40vh] flex items-center justify-center overflow-hidden mb-12">
          <div className="absolute inset-0">
            <img
              src="/images/scenery 1.jpg"
              alt="Gallery hero image"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
          <div className="relative z-10 text-center text-white px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-4"
            >
              Our Gallery
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl max-w-2xl mx-auto"
            >
              Capturing moments of adventure and beauty at Loch Lomond
            </motion.p>
          </div>
          
          {/* Wave Shape */}
          <div className="absolute bottom-0 left-0 w-full">
            <svg
              className="w-full h-auto"
              viewBox="0 0 1440 320"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#00B4D8"
                fillOpacity="0.8"
                d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
              ></path>
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg shadow-lg"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="text-sm font-medium">{image.alt}</p>
                    <p className="text-xs text-gray-300 capitalize">{image.category}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Grid */}
      {/* Adds space below the image grid so it doesn't touch the next section */}
      <div className="mt-16 px-8 md:px-16 mb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl"
        >
          <img
            src="/images/scenery2.jpg"
            alt="Anchor Management at Loch Lomond"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>

      {/* Location Section */}
      <section className="py-20 pb-32 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-white">Our Location</h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d17389.123456789012!2d-4.6082!3d56.0819!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4889c3c0c0c0c0c0%3A0x0!2sLoch%20Lomond!5e0!3m2!1sen!2suk!4v1234567890123!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Loch Lomond Location Map"
              ></iframe>
            </div>
            <div className="mt-8 text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300">
                Located in the heart of the Trossachs National Park, our base at Loch Lomond offers easy access to some of Scotland's most beautiful fishing spots.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 